/** @jsx isolateComponent */
import { css } from '@emotion/core';
import { Animation, ColorName, Colors } from '../../../theme';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { clamp } from '../../../utils/clamp';

type Props = {
  /**
   * The color of the progress bar.
   * @default 'blue500'
   */
  color?: ColorName;
  /**
   * The maximum value for the progress bar.
   * @default 1
   */
  max?: number;
  /**
   * The minimum value for the progress bar.
   * @default 0
   */
  min?: number;
  /**
   * The amount of the progress bar. Should be between `min` and `max`.
   */
  value: number;
};

const Styles = labelStyles('ProgressBar', {
  root: () =>
    css({
      position: 'relative',
      height: 5,
      borderRadius: 2.5,
      backgroundColor: Colors.grey200,
    }),
  progress: (color: ColorName) => [
    css({
      height: 'inherit',
      borderRadius: 'inherit',
      position: 'absolute',
      top: 0,
      left: 0,
      maxWidth: '100%',
      transition: `all ${Animation.transitionDuration} ease-in-out`,
      backgroundColor: Colors[color],
    }),
  ],
});

/**
 * Basic progress component. Shows how full something is.
 */
export const ProgressBar = ({
  color = 'blue500',
  max = 1,
  min = 0,
  ...props
}: Props) => {
  const value = clamp(props.value, min, max);
  const range = max - min;
  const decimal = (value - min) / range;
  const width = `${decimal * 100}%`;
  return (
    <div css={Styles.root()}>
      <div
        css={Styles.progress(color)}
        data-testid="progress"
        style={{
          width,
        }}
      />
    </div>
  );
};
